import {
  Button,
  Input,
  Typography,
  Spin,
  Steps,
  Tooltip,
  message,
  Tag,
  Divider,
} from "antd";
import {
  createSource,
  getSource,
  updateSource,
} from "pages/api-clients/integrations";
import React from "react";
import { useOrg } from "src/hooks/useOrg";
import {
  Source,
  SourceType,
} from "pages/api/[orgId]/integrations-client/definitions";
import { Terminology } from "./Terminology";

const { Title, Text } = Typography;

enum IntegrationState {
  fetching,
  none,
  creating,
  noSecret,
  active,
  savingGitlab,
}

export function GitlabIntegrationStatus() {
  const { id: orgId } = useOrg();
  const [gitlabSource, setGitlabSource] = React.useState<Source | null>(null);
  const [state, setState] = React.useState<IntegrationState>(
    IntegrationState.fetching
  );
  const [secret, setSecret] = React.useState("");
  const [authToken, setAuthToken] = React.useState("");
  const [scope, setScope] = React.useState({ orgs: [] } as Record<string, any>);

  const fetchedOnce = React.useRef(false);

  const listWebhookItems = [
    {
      description: (
        <p>
          Follow the instructions provided in the{" "}
          <a
            href="https://docs.gitlab.com/ee/user/project/integrations/webhooks.html#create-a-webhook"
            target="_blank"
            rel="noopener noreferrer"
          >
            gitlab webhook documentation
          </a>
          <br />
          For <strong>URL</strong> use:
          <Text code copyable>
            {`https://int.vzbl.dev/api/v1/gitlab/webhook/${gitlabSource?.uuid}`}
          </Text>
        </p>
      ),
    },
    {
      description: (
        <p>
          For <strong>Secret token</strong> add a secret of your choice. Paste
          that secret below under “Webhook secret”, and click ”Save”
        </p>
      ),
    },
    {
      description: <>Select all events</>,
    },
    { description: "Enable SSL verification" },

    { description: "Click ”Add Webhook” " },
  ];

  const listTokenItems = [
    {
      description: (
        <p>
          access your gitlab profile{" "}
          <a
            href="https://gitlab.com/-/user_settings/personal_access_tokens"
            target="_blank"
            rel="noopener noreferrer"
          >
            access tokens
          </a>
        </p>
      ),
    },
    {
      description: "Click 'Add new token'",
    },
    {
      description: (
        <>
          Set &quot;no expiration date&quot; if available, or any date you see
          fit
        </>
      ),
    },
    {
      description: (
        <>Select scopes: read_api, read_user, read_repository, read_registry</>
      ),
    },
    {
      description: (
        <>Scroll down and click &quot;Create personal access token&quot;</>
      ),
    },
    {
      description: (
        <>
          {" "}
          Copy the token, paste it below under &quot;Auth token&quot;, then
          click &quot;save&quot;
        </>
      ),
    },
  ];

  React.useEffect(() => {
    if (!fetchedOnce.current) {
      return;
    }

    if (gitlabSource == null) {
      setState(IntegrationState.none);
    }
  }, [gitlabSource]);

  const transitionRef = React.useRef({ gitlabSource, orgId });
  transitionRef.current = { gitlabSource, orgId };
  React.useEffect(() => {
    const { gitlabSource, orgId } = transitionRef.current;
    if (!orgId) return;
    switch (state) {
      case IntegrationState.fetching: {
        getSource(orgId, SourceType.Gitlab)
          .then((payload) => {
            const source = payload.data?.[0];
            fetchedOnce.current = true;
            setGitlabSource(source);
            setAuthToken(source?.authToken || "");
            setSecret(source?.secret || "");
            setScope(source?.scope);
          })
          .then(() => {
            setState(IntegrationState.active);
          });
        break;
      }
      case IntegrationState.creating: {
        createSource(orgId, SourceType.Gitlab).then((payload) => {
          setGitlabSource(payload.data);
        });
        break;
      }
      case IntegrationState.savingGitlab: {
        const payload: {
          id: string;
          secret?: string;
          authToken?: string;
          scope?: object;
        } = {
          id: gitlabSource!.id,
        };
        if (authToken) {
          payload.authToken = authToken;
        }
        if (secret) {
          payload.secret = secret;
        }
        if (scope) {
          payload.scope = scope;
        }

        updateSource(orgId, SourceType.Gitlab, payload).then((payload) => {
          setGitlabSource(payload.data);
          setState(IntegrationState.fetching);
          message.success("Successfully updated gitlab integration");
          setAuthToken(payload.data?.authToken || "");
          setSecret(payload.data?.secret || "");
          setScope(payload.data?.scope);
        });
        break;
      }
    }
  }, [state, authToken, secret, scope]);

  return [
    IntegrationState.fetching,
    IntegrationState.creating,
    IntegrationState.savingGitlab,
  ].includes(state) ? (
    <Spin />
  ) : state == IntegrationState.none ? (
    <Button
      onClick={() => {
        setState(IntegrationState.creating);
      }}
    >
      Create Gitlab Integration
    </Button>
  ) : (
    <div>
      <Terminology>
        <p>
          This page will guide you through setting up a webhook and/or auth
          token.
        </p>
        <p>
          <Tag bordered={false}>Gitlab Webhook</Tag>
          <ul>
            <li>Follow steps to create and save a secret</li>
            <li>Used to obtain always up-to-date data</li>
          </ul>
          <Tag bordered={false}>Auth Token</Tag>
          <ul>
            <li>Follow steps to create and save a token string</li>
            <li>Used to obtain historical data</li>
          </ul>
        </p>
        <p>
          One may be enough to get started, but for best results set up both.
        </p>
      </Terminology>
      <Divider />
      <h3>
        <Tag bordered={false}>Gitlab Webhook</Tag> configuration
      </h3>
      <div style={{ padding: "16px 40px" }}>
        <Steps
          progressDot
          direction="vertical"
          items={listWebhookItems}
          current={listWebhookItems.length - 1}
        />
      </div>
      <Tooltip
        placement="topLeft"
        title="Used to validate that payloads originate from gitlab"
      >
        <Title level={5}>
          <Tag bordered={false}>Gitlab Webhook</Tag> secret
        </Title>
        <Input.Password
          defaultValue={secret}
          allowClear
          visibilityToggle={secret != "placeholder"}
          style={{ width: "40%" }}
          onChange={(event) => setSecret(event.target.value)}
        />
        <Button
          type="primary"
          style={{ marginLeft: "10px" }}
          onClick={() => {
            setState(IntegrationState.savingGitlab);
          }}
          disabled={!secret}
        >
          Save
        </Button>
      </Tooltip>

      <Divider />
      <h3>
        <Tag bordered={false}>Auth Token</Tag> configuration
      </h3>
      <div style={{ padding: "16px 32px" }}>
        <Steps
          progressDot
          direction="vertical"
          items={listTokenItems}
          current={listTokenItems.length - 1}
        />
      </div>
      <Tooltip
        placement="topLeft"
        title="Enable more insights by letting VZBL pull your diffs and summarizing them"
      >
        <Title level={5}>Auth token</Title>
        <Input.Password
          allowClear
          defaultValue={authToken}
          visibilityToggle={authToken != "placeholder"}
          style={{ width: "80%" }}
          onChange={(event) => setAuthToken(event.target.value)}
        />
        <Button
          type="primary"
          style={{ marginLeft: "10px" }}
          onClick={() => {
            setState(IntegrationState.savingGitlab);
          }}
          disabled={!secret}
        >
          Save
        </Button>
      </Tooltip>
      <Tooltip
        placement="topLeft"
        title="Optional - comma-separated list of which gitlab namespaces to pull (useful if your token has access to several namespaces)"
      >
        <Title level={5}>Gitlab namespaces</Title>
        <Input
          allowClear
          defaultValue={((gitlabSource!.scope as any) || { orgs: [] })["orgs"]}
          style={{ width: "80%" }}
          onChange={(event) =>
            setScope({
              orgs: event.target.value.split(",").map((s) => s.trim()),
            })
          }
        />
        <Button
          type="primary"
          style={{ marginLeft: "10px" }}
          onClick={() => {
            setState(IntegrationState.savingGitlab);
          }}
          disabled={!authToken}
        >
          Save
        </Button>
      </Tooltip>
    </div>
  );
}
